import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const reportTypeSlice = createSlice({
    name: 'reportType',
    initialState: {
        pending: false,
        savePending: false,
        reportTypes: [],
        error: null,
        currentPage: 0,
        totalPages: 0,
        count: 0,
        total: 0,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        setReportTypes: (state, action) => {
            state.pending = false;
            state.reportTypes = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
            state.count = action.payload.count;
            state.total = action.payload.total;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        addReportType: (state, action) => {
            state.savePending = false;
            state.reportTypes = [action.payload, ...state.reportTypes];
        },
        editReportType: (state, action) => {
            state.savePending = false;
            const index = state.reportTypes.findIndex((reportType) => reportType.id === action.payload.id);
            if (index !== -1) {
                state.reportTypes = [
                    ...state.reportTypes.slice(0, index),
                    action.payload,
                    ...state.reportTypes.slice(index + 1),
                ];
            }
        },
        deleteReportType: (state, action) => {
            state.pending = false;
            const index = state.reportTypes.findIndex((reportType) => reportType.id === action.payload.id);
            if (index !== -1) {
                state.reportTypes = [...state.reportTypes.slice(0, index), ...state.reportTypes.slice(index + 1)];
            }
        },
        resetReportTypes: (state, action) => {
            state.reportTypes = [];
            state.currentPage = 0;
            state.totalPages = 0;
            state.count = 0;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

export const {
    start,
    stop,
    addReportType,
    setReportTypes,
    setError,
    editReportType,
    deleteReportType,
    resetReportTypes,
    startSave,
    stopSave,
    setSaveError,
} = reportTypeSlice.actions;

export const getReportTypes = (state) => state.reportType.reportTypes;
export const getReportTypesTotal = (state) => state.reportType.total;
export const getReportTypesTotalPages = (state) => state.reportType.totalPages;
export const getReportTypesCurrentPage = (state) => state.reportType.currentPage;
export const getReportTypesPending = (state) => state.reportType.pending;
export const getReportTypesError = (state) => state.reportType.error;
export const getReportTypesSavePending = (state) => state.reportType.savePending;

export const fetchReportTypesAction =
    ({ queryParams, onSuccess, onFailure, options } = {}) =>
    async (dispatch) => {
        const shouldUseState = options?.useState ?? true;
        try {
            if (shouldUseState) {
                dispatch(start());
            }
            let query = transformFiltersToQuery(queryParams);
            api.get('report-type' + query)
                .then((response) => {
                    if (shouldUseState) {
                        dispatch(setReportTypes(response.data));
                    }
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    if (shouldUseState) {
                        dispatch(setError(error));
                    }
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            if (shouldUseState) {
                dispatch(stop());
            }
            return console.error('error', e);
        }
    };

export const addReportTypeAction = (reportTypeDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSave());
        api.post(`report-type`, reportTypeDto)
            .then((response) => {
                dispatch(addReportType(response.data));
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const editReportTypeAction = (id, reportTypeDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSave());
        api.put('report-type/' + id, reportTypeDto)
            .then((response) => {
                dispatch(editReportType(response.data));
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stopSave());
        return console.error('error', e);
    }
};

export const deleteReportTypeAction = (reportTypeId, success, failure) => async (dispatch) => {
    try {
        dispatch(start());
        api.delete(`report-type/${reportTypeId}`)
            .then((response) => {
                dispatch(deleteReportType(response.data));
                if (success) {
                    success();
                }
                return response;
            })
            .catch((error) => {
                dispatch(setError(error));
                if (failure) {
                    failure(error);
                }
            });
    } catch (e) {
        dispatch(stop());
        return console.error('error', e);
    }
};

export default reportTypeSlice.reducer;
